import { useState } from "react";
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import shareIcon from "@assets/icon-share.svg";
import {
  faFacebookF,
  faLinkedinIn,
  faWhatsapp,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faPaperPlane,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

const buttonClass =
  "shrink-0 rounded-full !bg-gray-250 w-10 h-10 hover:opacity-70";

const buttonClassHeader =
  "rounded-full w-10 h-10 hover:opacity-70 !border-solid !border !border-gray-175";

type ArticleSocialSharing = {
  articleTitle: string;
  shareUrl?: string;
};

//TODO: Will change this when article v2 live
export default function ArticleSocialSharing({
  articleTitle,
  shareUrl = "",
}: ArticleSocialSharing): React.ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleHover = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {shareUrl ? (
        <div
          className="relative cursor-pointer"
          onMouseEnter={handleHover}
          onMouseLeave={handleHover}
          data-testid="article-social-sharing-component"
        >
          <img
            src={shareIcon}
            width={40}
            height={40}
            className="hover:opacity-70"
            alt="Share this article."
          />
          {isOpen ? (
            <div
              className={cx(
                "absolute right-0 top-full z-50 flex space-x-2 border-t border-gray-250 bg-white px-4 py-3 shadow-lg",
                "lg:right-[unset] lg:-translate-x-1/2"
              )}
            >
              <FacebookShareButton url={shareUrl} className={buttonClass}>
                <FontAwesomeIcon icon={faFacebookF} />
              </FacebookShareButton>

              <TwitterShareButton
                url={shareUrl}
                title={articleTitle}
                className={buttonClass}
              >
                <FontAwesomeIcon icon={faXTwitter} />
              </TwitterShareButton>

              <WhatsappShareButton
                url={shareUrl}
                title={articleTitle}
                className={buttonClass}
              >
                <FontAwesomeIcon icon={faWhatsapp} size="lg" />
              </WhatsappShareButton>

              <LinkedinShareButton
                url={shareUrl}
                title={articleTitle}
                className={buttonClass}
              >
                <FontAwesomeIcon icon={faLinkedinIn} />
              </LinkedinShareButton>

              <TelegramShareButton
                url={shareUrl}
                title={articleTitle}
                className={buttonClass}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </TelegramShareButton>

              <EmailShareButton
                url={shareUrl}
                subject="Sharing from The Business Times"
                body={articleTitle}
                separator=" - "
                className={buttonClass}
              >
                <FontAwesomeIcon icon={faEnvelope} />
              </EmailShareButton>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export function ArticleSocialSharingHeader({
  articleTitle,
  shareUrl = "",
}: ArticleSocialSharing): React.ReactElement {
  const shareOnClick = () => {
    if (typeof navigator.share === "function" && shareUrl) {
      navigator.share({
        text: articleTitle, // Current page title
        url: shareUrl, // Current page URL
      });
    }
  };

  return (
    <>
      {shareUrl ? (
        <div
          className="group relative cursor-pointer"
          data-testid="article-social-sharing-component"
        >
          <div
            className="flex h-[40px] w-[40px] items-center justify-center
           rounded-full !border !border-solid !border-gray-175 hover:opacity-70 min-[1100px]:hidden"
            onClick={shareOnClick}
          >
            <FontAwesomeIcon icon={faShare} />
          </div>

          <div
            className={cx(
              "invisible right-0 top-full z-50 gap-2 group-hover:opacity-100 lg:flex min-[1100px]:visible",
              "absolute right-0 top-full z-50 flex space-x-2 border-t border-gray-250 bg-white px-4 py-4 shadow-lg min-[1100px]:relative min-[1100px]:border-none min-[1100px]:py-0 min-[1100px]:shadow-none",
              { "group-hover:visible": typeof navigator.share !== "function" }
            )}
          >
            <FacebookShareButton url={shareUrl} className={buttonClassHeader}>
              <FontAwesomeIcon icon={faFacebookF} />
            </FacebookShareButton>

            <TwitterShareButton
              url={shareUrl}
              title={articleTitle}
              className={buttonClassHeader}
            >
              <FontAwesomeIcon icon={faXTwitter} />
            </TwitterShareButton>

            <WhatsappShareButton
              url={shareUrl}
              title={articleTitle}
              className={buttonClassHeader}
            >
              <FontAwesomeIcon icon={faWhatsapp} size="lg" />
            </WhatsappShareButton>

            <LinkedinShareButton
              url={shareUrl}
              title={articleTitle}
              className={buttonClassHeader}
            >
              <FontAwesomeIcon icon={faLinkedinIn} />
            </LinkedinShareButton>

            <TelegramShareButton
              url={shareUrl}
              title={articleTitle}
              className={buttonClassHeader}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </TelegramShareButton>

            <EmailShareButton
              url={shareUrl}
              subject="Sharing from The Business Times"
              body={articleTitle}
              separator=" - "
              className={cx(buttonClassHeader, "block lg:hidden")}
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </EmailShareButton>
          </div>
        </div>
      ) : null}
    </>
  );
}
